import React, { useContext, useEffect, useRef, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import clsx from 'clsx';
import MUIDataTable from 'mui-datatables';
import {
  CircularProgress,
  Divider,
  FormControlLabel,
  FormControl,
  Grid,
  Typography,
  TextField,
  Radio,
  RadioGroup,
  Button,
  IconButton,
  MenuItem,
  Tabs,
  Tab,
  Tooltip,
  FormHelperText,
  Select,
  Paper,
} from '@material-ui/core';
import {
  AddRounded as AddRoundedIcon,
  Clear as ClearIcon,
  Info as InfoIcon,
} from '@material-ui/icons';
import ImportDropdownButton from 'Views/Plans/CreatePlan/components/ImportDropdownButton/ImportDropdownButton';

import { SectionHeader } from 'components';
import { Context } from 'context';
import { ADD_NOTIFICATION } from 'context/actions';
import { PlansContext } from 'Views/Plans/context';
import {
  UPDATE_CATEGORY_DISCOUNTS,
  RESET_CATEGORY_DISCOUNTS,
  HIGHLIGHT_ERRORS,
} from 'Views/Plans/context/actions';
import { isSubscribiliUser } from 'Shared/Utils';
import { PLAN_SERVICES, USER_SERVICES } from 'Services';
import { downloadFile, sortCategoriesByOrder, assignCategoryOrderByPosition } from 'utils';
import { CPT_CUSTOM_DISPLAY_LIST } from 'Views/Common/enum';
import useStyles from './styles';
import DnDTable from './DnDTable';
import { useCountryConfig } from 'hooks/useCountryConfig';
import {
  toSlug,
  normalizeCategoryOrderInServices,
  normalizeCategoryOrderInCustomCodes,
  buildServicesPayload,
  transformImportedCustomCodes,
} from 'Views/Plans/utils/categoryOrderHelpers';

const ALLOWED_IMPORT_MIME_TYPES = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel.sheet.macroEnabled.12',
];

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_FILE_SIZE_MB = 5;
const CUSTOM_CODES_FILE_TYPES = ['XLSX', 'XLSM'];

const CUSTOM_CODES_TEMPLATE_FILE = 'Custom Code Upload.xlsx';
const CUSTOM_CODES_TEMPLATE_PATH = `${process.env.PUBLIC_URL || ''}/templates/${encodeURIComponent(CUSTOM_CODES_TEMPLATE_FILE)}`;

const formatRange = (start, end) => {
  const s = (start ?? '').toString().trim();
  const e = (end ?? '').toString().trim();
  if (!s && !e) return '';
  if (s && e) return `${s} - ${e}`;
  return s || e;
};

const formatDisplayRange = (start, end) => {
  const s = (start ?? '').toString().trim();
  const e = (end ?? '').toString().trim();
  if (!s && !e) return '';
  if (s && e) return `${s} - ${e}`;
  if (s) return `${s} -`;
  return `- ${e}`;
};

const hasChangedFromInitial = (hasInitData, currentValue, initialValue) =>
  hasInitData && !_.isEqual(currentValue, initialValue);

// Extracted table cell render components
const ServiceCategoryOrderCell = ({ rowIndex, flatServicesData, serviceGroups }) => {
  const currentOrderNumber = flatServicesData[rowIndex]?.orderNumber;
  const groupIndex = serviceGroups.findIndex(group => group.orderNumber === currentOrderNumber);
  const listNumber = groupIndex !== -1 ? groupIndex + 1 : 1;

  return listNumber;
};

const ServiceActionsCell = ({ rowIndex, onDelete, onAdd, disabled }) => (
  <div className="d-flex flex-no-wrap">
    {rowIndex >= 0 ? (
      <>
        <IconButton
          aria-label="add-subcode"
          onClick={() => onAdd(rowIndex)}
          data-testid={`add-custom-subcode-button-${rowIndex}`}
          disabled={disabled}
        >
          <AddRoundedIcon fontSize="inherit" />
        </IconButton>
        <IconButton
          aria-label="delete"
          onClick={() => onDelete(rowIndex)}
          data-testid={`delete-custom-code-button-${rowIndex}`}
          disabled={disabled}
        >
          <ClearIcon fontSize="inherit" />
        </IconButton>
      </>
    ) : null}
  </div>
);

const CustomCodeCategoryOrderCell = ({ rowIndex, flatCustomCodes, categoryGroups }) => {
  const currentOrderNumber = flatCustomCodes[rowIndex]?.orderNumber;
  const groupIndex = categoryGroups.findIndex(group => group.orderNumber === currentOrderNumber);
  const listNumber = groupIndex !== -1 ? groupIndex + 1 : 1;

  return listNumber;
};

const CustomCodeActionsCell = ({ rowIndex, onDelete, onAdd, disabled, showAdd }) => (
  <div className="d-flex flex-no-wrap">
    {rowIndex >= 0 && (
      <>
        {showAdd !== false && (
          <IconButton
            aria-label="add-subcode"
            onClick={() => onAdd(rowIndex)}
            data-testid={`add-custom-subcode-button-${rowIndex}`}
            disabled={disabled}
          >
            <AddRoundedIcon fontSize="inherit" />
          </IconButton>
        )}
        <IconButton
          aria-label="delete"
          onClick={() => onDelete(rowIndex)}
          data-testid={`delete-custom-code-button-${rowIndex}`}
          disabled={disabled}
        >
          <ClearIcon fontSize="inherit" />
        </IconButton>
      </>
    )}
  </div>
);

ServiceCategoryOrderCell.propTypes = {
  rowIndex: PropTypes.number.isRequired,
  flatServicesData: PropTypes.array.isRequired,
  serviceGroups: PropTypes.array.isRequired,
};

ServiceActionsCell.propTypes = {
  rowIndex: PropTypes.number.isRequired,
  onDelete: PropTypes.func.isRequired,
  onAdd: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};

CustomCodeCategoryOrderCell.propTypes = {
  rowIndex: PropTypes.number.isRequired,
  flatCustomCodes: PropTypes.array.isRequired,
  categoryGroups: PropTypes.array.isRequired,
};

CustomCodeActionsCell.propTypes = {
  rowIndex: PropTypes.number.isRequired,
  onDelete: PropTypes.func.isRequired,
  onAdd: PropTypes.func,
  disabled: PropTypes.bool,
  showAdd: PropTypes.bool,
};

// Extracted component definitions for render functions
const TextInputComponent = ({ value, name, rowId, onChange, error, inputProps = {}, classes, ...props }) => (
  <TextField
    required
    fullWidth
    variant="outlined"
    value={value === '-' ? '' : value}
    name={name}
    onChange={onChange}
    error={error}
    className={classes?.textFieldInput}
    inputProps={{
      'data-testid': `category-input-${name}-${rowId}`,
      ...inputProps,
    }}
    {...props}
  />
);

TextInputComponent.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string.isRequired,
  rowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  onChange: PropTypes.func,
  error: PropTypes.bool,
  inputProps: PropTypes.object,
  classes: PropTypes.object,
};

const ToggleComponent = ({ currentValue, onToggleChange, disabled, dIndex, isCustomCode, classes }) => {
  const { config: countryConfig } = useCountryConfig();
  const DISCOUNT_TYPES = {
    discount_percent: '%',
    flat_price: countryConfig?.currency?.symbol || '$',
    discount_price: `${countryConfig?.currency?.symbol} off`,
  };
  return (
  <Grid container className={classes?.tabsContainer}>
    <Tabs
      value={currentValue}
      indicatorColor="secondary"
      textColor="secondary"
      onChange={(_event, newValue) => onToggleChange(newValue, dIndex, isCustomCode)}
      variant="scrollable"
      scrollButtons="auto"
      className={classes?.discountTabs}
    >
      {Object.keys(DISCOUNT_TYPES).map((indKey) => (
        <Tab
          key={indKey}
          value={indKey}
          label={DISCOUNT_TYPES[indKey]}
          disabled={disabled}
        />
      ))}
    </Tabs>
  </Grid>
  );
};

ToggleComponent.propTypes = {
  currentValue: PropTypes.string,
  onToggleChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  dIndex: PropTypes.number.isRequired,
  isCustomCode: PropTypes.bool,
  classes: PropTypes.object,
};

const DisplaySelectComponent = ({ value, name, rowIndex, onChange, disabled, options }) => (
  <FormControl fullWidth margin="dense" variant="outlined">
    <Select
      value={value || 'fees_list'}
      name={name}
      onChange={onChange}
      disabled={disabled}
    >
      {Object.keys(options).map((item, itemIndex) => (
        <MenuItem
          key={item}
          value={item}
          data-testid={`${name}-display-select-item-${itemIndex + 1}`}
        >
          {options[item]}
        </MenuItem>
      ))}
    </Select>
  </FormControl>
);

DisplaySelectComponent.propTypes = {
  value: PropTypes.string,
  name: PropTypes.string.isRequired,
  rowIndex: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  options: PropTypes.object.isRequired,
};

// Extracted table cell renderers
const CategoryOrderCellRenderer = ({ rowIndex, flatServicesData, serviceGroups }) => (
  <ServiceCategoryOrderCell
    rowIndex={rowIndex}
    flatServicesData={flatServicesData}
    serviceGroups={serviceGroups}
  />
);

CategoryOrderCellRenderer.propTypes = {
  rowIndex: PropTypes.number.isRequired,
  flatServicesData: PropTypes.array.isRequired,
  serviceGroups: PropTypes.array.isRequired,
};

const ActionsCellRenderer = ({ rowIndex, onDelete, onAdd, disabled }) => (
  <ServiceActionsCell
    rowIndex={rowIndex}
    onDelete={onDelete}
    onAdd={onAdd}
    disabled={disabled}
  />
);

ActionsCellRenderer.propTypes = {
  rowIndex: PropTypes.number.isRequired,
  onDelete: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  onAdd: PropTypes.func,
};

const CustomCodeCategoryOrderCellRenderer = ({ rowIndex, flatCustomCodes, categoryGroups }) => (
  <CustomCodeCategoryOrderCell
    rowIndex={rowIndex}
    flatCustomCodes={flatCustomCodes}
    categoryGroups={categoryGroups}
  />
);

CustomCodeCategoryOrderCellRenderer.propTypes = {
  rowIndex: PropTypes.number.isRequired,
  flatCustomCodes: PropTypes.array.isRequired,
  categoryGroups: PropTypes.array.isRequired,
};

const CustomCodeActionsCellRenderer = ({ rowIndex, onAdd, onDelete, disabled, showAdd }) => (
  <CustomCodeActionsCell
    rowIndex={rowIndex}
    onAdd={onAdd}
    onDelete={onDelete}
    disabled={disabled}
    showAdd={showAdd}
  />
);

CustomCodeActionsCellRenderer.propTypes = {
  rowIndex: PropTypes.number.isRequired,
  onAdd: PropTypes.func,
  onDelete: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  showAdd: PropTypes.bool,
};

const RowNumberCellRenderer = ({ rowIndex }) => rowIndex + 1;

RowNumberCellRenderer.propTypes = {
  rowIndex: PropTypes.number.isRequired,
};

const CategoryNameCellRenderer = ({
  value,
  rowIndex,
  flatData,
  fieldName,
  onChange,
  errorForm,
  classes
}) => {
  const isSameCategory = Boolean(
    rowIndex &&
    flatData[rowIndex - 1]?.orderNumber === flatData[rowIndex]?.orderNumber
  );

  if (isSameCategory) return null;

  return (
    <TextInputComponent
      value={flatData[rowIndex]?.[fieldName] || value}
      name={fieldName}
      rowId={rowIndex}
      onChange={(evt) => {
        onChange(evt, rowIndex);
      }}
      error={errorForm && !value}
      variant="outlined"
      classes={classes}
    />
  );
};

CategoryNameCellRenderer.propTypes = {
  value: PropTypes.string,
  rowIndex: PropTypes.number.isRequired,
  flatData: PropTypes.array.isRequired,
  fieldName: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  errorForm: PropTypes.bool,
  classes: PropTypes.object,
};

const TextFieldCellRenderer = ({
  value,
  name,
  rowIndex,
  onChange,
  errorForm,
  inputProps = {},
  classes,
  ...props
}) => (
  <TextInputComponent
    value={value}
    name={name}
    rowId={rowIndex}
    onChange={(evt) => onChange(evt, rowIndex)}
    error={
      errorForm &&
      (value === null || value === undefined || (typeof value === 'string' && value.trim() === ''))
    }
    inputProps={inputProps}
    classes={classes}
    {...props}
  />
);

TextFieldCellRenderer.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  name: PropTypes.string.isRequired,
  rowIndex: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  errorForm: PropTypes.bool,
  inputProps: PropTypes.object,
  classes: PropTypes.object,
};

const RangeInputCell = ({
  rangeStart,
  rangeEnd,
  rowIndex,
  onChange,
  classes,
  disabled,
  error,
  errorMessage,
}) => {
  const [localValue, setLocalValue] = useState(() => formatRange(rangeStart, rangeEnd));
  const prevRangeRef = useRef({ rangeStart, rangeEnd });

  useEffect(() => {
    if (
      prevRangeRef.current.rangeStart !== rangeStart ||
      prevRangeRef.current.rangeEnd !== rangeEnd
    ) {
      setLocalValue(formatRange(rangeStart, rangeEnd));
      prevRangeRef.current = { rangeStart, rangeEnd };
    }
  }, [rangeStart, rangeEnd]);

  const commitRangeValue = (value) => {
    onChange({ target: { name: 'code', value } }, rowIndex);
  };

  const handleBlur = () => {
    commitRangeValue(localValue);
  };

  return (
    <TextField
      required
      fullWidth
      variant="outlined"
      value={localValue}
      error={error}
      helperText={errorMessage}
      name="code"
      onChange={(e) => {
        setLocalValue(e.target.value);
      }}
      onBlur={handleBlur}
      className={classes?.textFieldInput}
      inputProps={{
        'data-testid': `category-input-code-${rowIndex}`,
        disabled,
        placeholder: 'Start - End',
      }}
    />
  );
};

RangeInputCell.propTypes = {
  rangeStart: PropTypes.string,
  rangeEnd: PropTypes.string,
  rowIndex: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  classes: PropTypes.object,
  disabled: PropTypes.bool,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
};

const ToggleCellRenderer = ({ rowIndex, isCustomCode, renderToggle }) =>
  renderToggle(rowIndex, isCustomCode);

ToggleCellRenderer.propTypes = {
  rowIndex: PropTypes.number.isRequired,
  isCustomCode: PropTypes.bool,
  renderToggle: PropTypes.func.isRequired,
};

const DisplaySelectCellRenderer = ({ value, name, rowIndex, renderSelect }) =>
  renderSelect(value, name, rowIndex);

DisplaySelectCellRenderer.propTypes = {
  value: PropTypes.string,
  name: PropTypes.string.isRequired,
  rowIndex: PropTypes.number.isRequired,
  renderSelect: PropTypes.func.isRequired,
};

const hasEnableLifeTimeValueChanged = (intialValues, currValues) => {
  return intialValues?.some(
    (item, index) =>
      item?.enableLifeTimeValue !== currValues?.[index]?.enableLifeTimeValue,
  );
};

const EMPTY_CPT_CODES = [];
const CategoryDiscounts = (props) => {
  const {
    level,
    levelId,
    planPackageId,
    otherButtons = '',
    setExecuteHandleSubmit,
    showCategoryDiscounts = true,
  } = props;

  const { state: { user }, dispatch: globalDispatch } = useContext(Context);

  const {
    state: {
      plan_id,
      highlightErrors,
      discounts = {},
      isPlansByExcel = false,
      payment_cpt_codes: exludedLifeTimeValueCodes = EMPTY_CPT_CODES
    },
    dispatch,
  } = useContext(PlansContext);
  const subscribiliUser = isSubscribiliUser(user);
  const viewOnly = isPlansByExcel;

  const {
    initData = {},
    discount_level = 'global',
    discount_value = '',
    categories: categoryList = [],
    services: serviceList = [],
    custom: customCodes = [],
    formUpdated = false,
    formValid = true,
  } = discounts;

  const [categoryLoading, setCategoryLoading] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [lifeTimeValueChanged, setLifeTimeValueChanged] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [importingFile, setImportingFile] = useState(false);
  const [templateDownloading, setTemplateDownloading] = useState(false);
  const fileInputRef = useRef(null);

  const noDiscount = discount_level === 'no_discount';
  const globalDiscount = discount_level === 'global';
  const categoryDiscount = discount_level === 'category';
  // Keep categories visible if a saved plan already uses them.
  const showCategories =
    showCategoryDiscounts || initData?.discount_level === 'category';

  const classes = useStyles();

  // Safely convert a value to a slug string
  // Transform nested custom codes structure to flat array for table display
  const flatCustomCodes = useMemo(() => {
    if (!customCodes || !Array.isArray(customCodes)) return [];

    return customCodes
      .flatMap((category, categoryIndex) => {
        const groupKey = category?.id || `local-${categoryIndex + 1}`;
        const categoryOrder =
          category?.category_order ?? category?.codes?.[0]?.category_order ?? categoryIndex + 1;
        return (category.codes || []).map((code) => ({
          ...code,
          categoryId: groupKey,
          categoryName: category.name || '',
          categoryOrder: code.category_order ?? categoryOrder,
          orderNumber: groupKey,
        }));
      })
      .sort((a, b) => (a.categoryOrder ?? 0) - (b.categoryOrder ?? 0));
  }, [customCodes]);

  // Group custom codes by category name for drag operations
  const categoryGroups = useMemo(() => {
    if (!flatCustomCodes || !Array.isArray(flatCustomCodes)) return [];

    // Group by stable category id (orderNumber now holds category id)
    const categoryMap = new Map();
    flatCustomCodes.forEach((code, index) => {
      if (!categoryMap.has(code.orderNumber)) { // orderNumber now contains category id
        categoryMap.set(code.orderNumber, []);
      }
      categoryMap.get(code.orderNumber).push({ ...code, originalIndex: index });
    });

    return Array.from(categoryMap.entries())
      .map(([categoryId, rows]) => ({
        orderNumber: categoryId, // This is the stable category id
        rows,
        startIndex: Math.min(...rows.map((r) => r.originalIndex)),
        // Stable draggable id that doesn't change with row count
        id: `category-${categoryId}`,
        // Keep current category name for display purposes
        groupName: rows[0]?.categoryName || '',
        height: rows.length * 60, // Consistent height calculation
      }))
      .sort((a, b) => {
        // Sort by the first row's categoryOrder to maintain proper order
        const aOrder = a.rows[0]?.categoryOrder || 0;
        const bOrder = b.rows[0]?.categoryOrder || 0;
        return aOrder - bOrder;
      });
  }, [flatCustomCodes]);



  // Transform services data for drag and drop (no auto-categorization)
  // SIMPLIFIED: Direct mapping like CPTValues (no complex transformations)
  const flatServicesData = useMemo(() => {
    if (!serviceList || !Array.isArray(serviceList)) return [];

    return serviceList.map((service, index) => {
      // Use category_order as the stable group key so that two rows with the same
      // category text but different category_orders (e.g. while the user is still
      // typing a new category name) are never merged into the same visual group.
      // Fall back to a unique per-row key for rows with no category_order.
      const orderNumber =
        service.category_order == null
          ? `row-${index}`
          : `order-${service.category_order}`;
      return {
        ...service,
        actions: service,
        display_type: service.display_type || 'fees_list',
        categoryName: service.category || '',
        orderNumber,
        discount_value: service.discount_value || 0,
      };
    });
  }, [serviceList]);

  // Group services by category_order-based orderNumber for drag operations
  const serviceGroups = useMemo(() => {
    if (!flatServicesData || !Array.isArray(flatServicesData)) return [];

    const categoryMap = new Map();
    flatServicesData.forEach((service, index) => {
      if (!categoryMap.has(service.orderNumber)) {
        categoryMap.set(service.orderNumber, []);
      }
      categoryMap.get(service.orderNumber).push({ ...service, originalIndex: index });
    });

    return Array.from(categoryMap.entries())
      .map(([orderNumber, rows]) => ({
        orderNumber,
        rows,
        startIndex: Math.min(...rows.map((r) => r.originalIndex)),
        id: `service-category-${orderNumber}`,
        groupName: rows[0]?.categoryName || '',
        height: rows.length * 60,
      }))
      .sort((a, b) => {
        const aOrder = a.rows[0]?.category_order ?? a.rows[0]?.originalIndex ?? 0;
        const bOrder = b.rows[0]?.category_order ?? b.rows[0]?.originalIndex ?? 0;
        return aOrder - bOrder;
      });
  }, [flatServicesData]);

  const dispatchGlobalNotification = (severity, message) =>
    globalDispatch({
      type: ADD_NOTIFICATION,
      payload: { notification: { severity, message } },
    });

  const updateCategoryValues = useCallback((updatedValues) => {
    const normalized = { ...updatedValues };
    // Always normalize category_order before writing to state so that
    // data arriving without the field (from any code path) never causes
    // sort/lookup failures downstream.
    if (Array.isArray(normalized.custom)) {
      normalized.custom = normalizeCategoryOrderInCustomCodes(normalized.custom);
    }
    if (Array.isArray(normalized.services)) {
      normalized.services = normalizeCategoryOrderInServices(normalized.services);
    }
    dispatch({
      type: UPDATE_CATEGORY_DISCOUNTS,
      payload: normalized,
    });
  }, [dispatch]);

  const handleCollapseGroup = (orderNumber) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [orderNumber]: true,
    }));
  };

  const handleExpandGroup = (orderNumber) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [orderNumber]: false,
    }));
  };



  // Update custom codes structure from reordered category groups
  const updateCustomCodes = (newCategoryGroups) => {
    // Reconstruct the original nested structure based on category groups
    const codesByCategory = {};

    // Group the reordered codes back by their category id (orderNumber)
    newCategoryGroups.forEach((categoryGroup, groupIndex) => {
      const newCategoryOrder = groupIndex + 1; // Each group represents a category
      const categoryId = categoryGroup.orderNumber; // Stable category identifier
      const categoryName = categoryGroup.groupName || categoryGroup.rows?.[0]?.categoryName || '';

      categoryGroup.rows.forEach((code) => {
        if (!codesByCategory[categoryId]) {
          codesByCategory[categoryId] = {
            id: categoryId,
            name: categoryName,
            category_order: newCategoryOrder,
            codes: [],
          };
        }

        const {
          orderNumber,
          originalIndex,
          categoryOrder,
          ...restCode
        } = code;

        codesByCategory[categoryId].codes.push({
          ...restCode,
          category_order: newCategoryOrder,
        });
      });
    });

    const newCustomCodes = Object.values(codesByCategory).sort(
      (a, b) => (a.category_order ?? 1) - (b.category_order ?? 1)
    );

    updateCategoryValues({
      custom: newCustomCodes,
      formUpdated: true,
    });
  };

  // Update function for services drag and drop
  const updateServices = (newServiceGroups) => {
    // Flatten the grouped services back to the original serviceList structure
    const updatedServiceList = newServiceGroups.flatMap((group, groupIndex) =>
      group.rows.map(service => {
        // Remove all the added frontend DnD properties and keep only original service fields
        const {
          actions,
          originalIndex,
          orderNumber,
          categoryName,
          categoryOrder,
          ...originalService
        } = service;

        return {
          ...originalService,
          order: groupIndex + 1, // Update order based on new position
          // Ensure display_type is preserved if it was in original data
          display_type: service.display_type || originalService.display_type || 'fees_list',
        };
      })
    );

    updateCategoryValues({
      services: updatedServiceList,
    });
  };

  const updateCategories = (newCategoryGroups) => {
    const updatedCategoryList = newCategoryGroups.map((group, groupIndex) => {
      const row = group?.rows?.[0] || {};
      const {
        actions,
        code,
        orderNumber,
        originalIndex,
        ...originalCategory
      } = row;

      const nextOrder = groupIndex + 1;
      return {
        ...originalCategory,
        category_order: nextOrder,
        order: nextOrder,
      };
    });

    updateCategoryValues({
      categories: updatedCategoryList,
      formUpdated: true,
    });
  };

  const onTextfieldChange = (evt, dIndex) => {
    const { name: dName, value: dValue } = evt?.target || {};

    // Fall back to '' instead of '0' so users can clear the discount field
    let replacedValue =
      dValue.toString().match(/^-?\d+(?:\.\d{0,2})?/)?.[0] || '';
    let parsedValue = replacedValue === '' ? '' : Number.parseFloat(replacedValue);
    parsedValue = parsedValue !== '' && parsedValue > 100 ? 100 : parsedValue;

    if (dIndex !== undefined) {
      const clonedValue = _.cloneDeep(categoryList);
      clonedValue[dIndex][dName] = parsedValue;

      updateCategoryValues({
        categories: clonedValue,
      });
    } else {
      const clonedValue = _.cloneDeep(categoryList);
      clonedValue.forEach(
        (category) => (category.discount_value = parsedValue),
      );

      updateCategoryValues({
        categories: clonedValue,
        discount_value: parsedValue,
      });
    }
  };

  const onCategoryFieldChange = (evt, dIndex) => {
    const { name: dName, value: dValue } = evt?.target || {};
    if (dIndex === undefined) return;

    const clonedValue = _.cloneDeep(categoryList);
    const category = clonedValue[dIndex];

    if (dName === 'code') {
      const codeValue = String(dValue || '');
      const separatorIndex = codeValue.indexOf('-');

      if (separatorIndex >= 0) {
        category.range_start = codeValue.slice(0, separatorIndex).trim();
        category.range_end = codeValue.slice(separatorIndex + 1).trim();
      } else {
        category.range_start = codeValue.trim();
        category.range_end = '';
      }
    } else {
      category[dName] = dValue;
    }

    updateCategoryValues({ categories: clonedValue });
  };

  const parseServiceFieldValue = (dName, dValue, max) => {
    if (dName === 'discount_value') {
      // Fall back to '' instead of '0' so users can clear the discount field
      return dValue?.toString()?.match(/^-?\d{0,5}(?:\.\d{0,2})?/)?.[0] || '';
    }
    if (max) {
      const dMax = Number.parseFloat(max);
      const clamped = parseFloat(dValue.toString().match(/^-?\d+(?:\.\d{0,2})?/)?.[0] || '0');
      return clamped > dMax ? dMax : clamped;
    }
    return dValue;
  };

  const applyCategoryNameChange = (services, dIndex, parsedValue) => {
    const previousName = serviceList?.[dIndex]?.category || '';
    const previousOrder = serviceList?.[dIndex]?.category_order ?? null;

    if (previousName.trim()) {
      // A group is uniquely identified by BOTH name AND category_order — only
      // update rows that belong to the exact same group as the edited row.
      services.forEach((service) => {
        const sameGroup =
          (service?.category || '') === previousName &&
          (service?.category_order ?? null) === previousOrder;
        if (sameGroup) service.category = parsedValue;
      });
    } else {
      // Row had no category yet — update only this row and give it a unique
      // category_order so it is never merged with another group.
      services[dIndex].category = parsedValue;
      if (services[dIndex].category_order == null) {
        const maxOrder = Math.max(0, ...services.map((s) => s.category_order ?? 0));
        services[dIndex].category_order = maxOrder + 1;
      }
    }
  };

  const onServiceFieldChange = (evt, dIndex) => {
    const { name: dName, value: dValue, max } = evt?.target || {};
    if (dIndex === undefined) return;

    const parsedValue = parseServiceFieldValue(dName, dValue, max);
    const clonedValue = _.cloneDeep(serviceList);

    if (dName === 'categoryName') {
      applyCategoryNameChange(clonedValue, dIndex, parsedValue);
    } else {
      clonedValue[dIndex][dName] = parsedValue;
    }

    updateCategoryValues({ services: clonedValue });
  };

  const handleRadioChange = (event) => {
    const targetValue = event?.target?.value || 'global';

    if (targetValue === 'global') {
      const clonedValue = _.cloneDeep(categoryList);
      clonedValue.forEach(
        (category) => (category.discount_value = discount_value),
      );

      updateCategoryValues({
        categories: clonedValue,
        discount_level: targetValue,
      });
    } else if (targetValue === 'category') {
      updateCategoryValues({
        discount_level: targetValue,
      });
      if (!categoryList || categoryList.length === 0) {
        importDefaultCategories();
      }
    } else {
      updateCategoryValues({
        discount_level: targetValue,
      });
    }
  };

  const deleteService = (delId) => {
    let clonedList = _.cloneDeep(serviceList);
    clonedList.splice(delId, 1);

    updateCategoryValues({
      services: clonedList,
    });
  };

  const deleteCategory = (delId) => {
    const clonedList = structuredClone(categoryList);
    clonedList.splice(delId, 1);
  
    updateCategoryValues({
      categories: clonedList,
    });
  };

  const addServiceRow = (rowIndex) => {
    const ref = flatServicesData?.[rowIndex];
    if (!ref) return;

    const cloned = _.cloneDeep(serviceList);

    const categoryName = ref.categoryName || ref.category || '';
    const categoryOrder = ref.category_order || ref.categoryOrder || ref.order || 1;

    const newService = {
      category: categoryName,
      category_order: categoryOrder,
      code: '',
      name: '',
      discount_type: 'discount_percent',
      discount_value: 0,
      display_type: 'fees_list',
    };

    // Insert right after the clicked row index
    cloned.splice(rowIndex + 1, 0, newService);

    updateCategoryValues({
      services: cloned,
    });
  };

  const addEditCustomCode = () => {
    let existingCustom = Array.isArray(customCodes) ? customCodes : [];
    
    // CRITICAL: If customCodes is empty but serviceList has services with categories,
    // those services are being displayed as the "fallback" custom codes table.
    // We must convert them to proper custom codes first before adding the new one,
    // otherwise they'll disappear when custom codes becomes non-empty.
    if (existingCustom.length === 0) {
      const servicesWithCategories = serviceList.filter(
        (s) => s.category && String(s.category).trim() !== ''
      );
      if (servicesWithCategories.length > 0) {
        existingCustom = convertServicesToCustomCodes(servicesWithCategories);
      }
    }
    
    const clonedCustom = structuredClone(existingCustom);

    // Find max category_order across existing custom codes to avoid collision
    const maxCategoryOrder = Math.max(
      0,
      ...clonedCustom.map(
        (cat) => cat?.category_order ?? cat?.codes?.[0]?.category_order ?? 0
      )
    );
    const nextCategoryOrder = maxCategoryOrder + 1;

    const newCategoryId = `local-${Date.now()}`;
    const newCategoryName = `Category ${nextCategoryOrder}`;

    const newCode = {
      id: `code-${Date.now()}`,
      code: '',
      description: '',
      discount_type: 'discount_percent',
      discount_value: 0,
      display_type: 'fees_list',
      category_order: nextCategoryOrder,
    };

    clonedCustom.push({
      id: newCategoryId,
      name: newCategoryName,
      category_order: nextCategoryOrder,
      codes: [newCode],
    });

    // When we convert services to custom, we also need to remove those services
    // from the services array to avoid duplication.
    const updatedServices = existingCustom.length > 0 && existingCustom !== customCodes
      ? serviceList.filter((s) => !s.category || String(s.category).trim() === '')
      : undefined;

    updateCategoryValues({ 
      custom: clonedCustom, 
      ...(updatedServices !== undefined ? { services: updatedServices } : {}),
      formUpdated: true 
    });
  };

  const addCustomCode = () => {
    // SIMPLIFIED: Use services array like CPTValues uses cpt_codes array
    const clonedServices = _.cloneDeep(serviceList);
    
    // Find the highest category_order (like CPTValues uses 'list' number)
    const maxCategoryOrder = Math.max(
      ...clonedServices.map(service => service.category_order || 0),
      0
    );
    const nextCategoryOrder = maxCategoryOrder + 1;
    
    // Auto-generate unique category name to prevent grouping under "Uncategorized"
    const newCategoryName = `Category ${nextCategoryOrder}`;
    
    // Simple new service entry (like CPTValues adds new cpt_code)
    const newService = {
      category: newCategoryName,
      category_order: nextCategoryOrder,
      code: "",
      name: "",
      discount_type: "discount_percent",
      discount_value: 0,
    };
    clonedServices.push(newService);
    
    updateCategoryValues({
      services: clonedServices,
    });
  };

  const handleImportFile = (evt) => {
    const file = evt?.target?.files?.[0];
    if (!file || importingFile) return;
    if (!ALLOWED_IMPORT_MIME_TYPES.includes(file.type)) {
      dispatchGlobalNotification(
        'error',
        'Please select an Excel file.',
      );
      evt.target.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      dispatchGlobalNotification(
        'error',
        'File is too large. Maximum size is 5MB.',
      );
      evt.target.value = '';
      return;
    }
    setImportingFile(true);
    const formData = new FormData();
    formData.append('file', file);
    PLAN_SERVICES.processCustomCodeTemplate(formData)
      .then((response) => {
        if (response?.type === 'success' && Array.isArray(response?.data)) {
          const transformed = transformImportedCustomCodes(response.data);
          const normalized = normalizeCategoryOrderInCustomCodes(transformed);
          updateCategoryValues({ custom: normalized, formUpdated: true });
          dispatchGlobalNotification('success', 'Custom codes imported.');
        } else {
          dispatchGlobalNotification('error', response?.message || 'Import failed.');
        }
      })
      .catch(() => {
        dispatchGlobalNotification('error', 'Import failed.');
      })
      .finally(() => {
        setImportingFile(false);
        evt.target.value = '';
      });
  };

  const handleCustomCodesTemplateDownload = async () => {
    if (templateDownloading) return;
    setTemplateDownloading(true);
    try {
      const res = await fetch(CUSTOM_CODES_TEMPLATE_PATH);
      if (!res.ok) {
        throw new Error('Template file could not be loaded.');
      }
      const buf = await res.arrayBuffer();
      downloadFile(
        buf,
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        CUSTOM_CODES_TEMPLATE_FILE,
      );
      dispatchGlobalNotification('success', 'Template downloaded successfully.');
    } catch (err) {
      dispatchGlobalNotification(
        'error',
        err?.message || 'Error while downloading the sample template.',
      );
    } finally {
      setTemplateDownloading(false);
    }
  };

  const deleteCustomCode = (delId) => {
    if (delId === undefined || !flatCustomCodes[delId]) return;

    const codeToDelete = flatCustomCodes[delId];
    const clonedCustomCodes = _.cloneDeep(customCodes);

    const categoryIndex = clonedCustomCodes.findIndex(cat => cat.id === codeToDelete.categoryId);
    if (categoryIndex !== -1) {
      const codeIndex = clonedCustomCodes[categoryIndex].codes.findIndex(code => code.id === codeToDelete.id);
      if (codeIndex !== -1) {
        clonedCustomCodes[categoryIndex].codes.splice(codeIndex, 1);

        // Remove category if no codes left
        if (clonedCustomCodes[categoryIndex].codes.length === 0) {
          clonedCustomCodes.splice(categoryIndex, 1);
        }

        updateCategoryValues({
          custom: clonedCustomCodes,
          formUpdated: true,
        });
      }
    }
  };

  const addCustomSubCode = (flatIndex) => {
    if (flatIndex === undefined || !flatCustomCodes[flatIndex]) return;
    const codeToClone = flatCustomCodes[flatIndex];
    const clonedCustom = _.cloneDeep(customCodes);
    const catIdx = clonedCustom.findIndex(c => c.id === codeToClone.categoryId);
    if (catIdx === -1) return;
    const categoryOrder = clonedCustom[catIdx]?.category_order ?? clonedCustom[catIdx]?.codes?.[0]?.category_order ?? 1;
    // Try to find exact code index by id
    let codeIndex = clonedCustom[catIdx].codes.findIndex(c => c.id === codeToClone.id);
    // Fallback: compute position within category in the flat list, and map to codes index
    if (codeIndex === -1) {
      const currCategoryId = codeToClone.categoryId;
      // Find the start index of this category block in the flat list
      let startFlatIdx = flatIndex;
      while (startFlatIdx - 1 >= 0 && flatCustomCodes[startFlatIdx - 1]?.categoryId === currCategoryId) {
        startFlatIdx -= 1;
      }
      const positionWithinCategory = flatIndex - startFlatIdx; // 0-based
      codeIndex = Math.min(positionWithinCategory, clonedCustom[catIdx].codes.length - 1);
    }
    const newCode = {
      id: `code-${Date.now()}`,
      code: '',
      description: '',
      discount_type: 'discount_percent',
      discount_value: 0,
      display_type: 'fees_list',
      category_order: categoryOrder,
    };
    const insertAt = codeIndex >= 0 ? codeIndex + 1 : clonedCustom[catIdx].codes.length;
    clonedCustom[catIdx].codes.splice(insertAt, 0, newCode);
    updateCategoryValues({ custom: clonedCustom, formUpdated: true });
  };


  const onCustomCodeFieldChange = (evt, dIndex) => {
    const { name: dName, value: dValue } = evt?.target || {};

    if (dIndex === undefined || !flatCustomCodes[dIndex]) return;

    let parsedValue = dValue;

    if (dName === 'discount_value') {
      // Fall back to '' instead of '0' so users can clear the discount field
      let replacedValue =
        dValue?.toString()?.match(/^-?\d{0,5}(?:\.\d{0,2})?/)?.[0] || '';
      parsedValue = replacedValue;
    }

    const codeToUpdate = flatCustomCodes[dIndex];
    const clonedCustomCodes = _.cloneDeep(customCodes);

    const categoryIndex = clonedCustomCodes.findIndex(cat => cat.id === codeToUpdate.categoryId);
    if (categoryIndex !== -1) {
      const codeIndex = clonedCustomCodes[categoryIndex].codes.findIndex(code => code.id === codeToUpdate.id);
      if (codeIndex !== -1) {
        // If changing category name, update the parent category only (not the code object)
        if (dName === 'categoryName') {
          clonedCustomCodes[categoryIndex].name = parsedValue;
        } else {
          clonedCustomCodes[categoryIndex].codes[codeIndex][dName] = parsedValue;
        }

        updateCategoryValues({
          custom: clonedCustomCodes,
          formUpdated: true,
        });
      }
    }
  };

  const handleToggleChange = (newValue, dIndex, isCustomCode) => {
    if (isCustomCode) {
      onCustomCodeFieldChange(
        { target: { name: 'discount_type', value: newValue } },
        dIndex,
      );
    } else {
      onServiceFieldChange(
        { target: { name: 'discount_type', value: newValue } },
        dIndex,
      );
    }
  };

  const renderToggle = (dIndex, isCustomCode = false) => {
    const disabled = subscribiliUser;
    const currentValue = isCustomCode
      ? flatCustomCodes?.[dIndex]?.discount_type
      : serviceList?.[dIndex]?.discount_type;

    return (
      <ToggleComponent
        currentValue={currentValue}
        onToggleChange={handleToggleChange}
        disabled={disabled}
        dIndex={dIndex}
        isCustomCode={isCustomCode}
        classes={classes}
      />
    );
  };

  const renderDisplaySelect = (value, name, rowIndex) => (
    <DisplaySelectComponent
      value={value}
      name={name}
      rowIndex={rowIndex}
      onChange={(event) =>
        onCustomCodeFieldChange(
          { target: { name: event.target.name, value: event.target.value } },
          rowIndex
        )
      }
      disabled={subscribiliUser}
      options={CPT_CUSTOM_DISPLAY_LIST}
    />
  );

  const renderServiceDisplaySelect = (value, name, rowIndex) => (
    <DisplaySelectComponent
      value={value}
      name={name}
      rowIndex={rowIndex}
      onChange={(event) =>
        onServiceFieldChange(
          { target: { name: event.target.name, value: event.target.value } },
          rowIndex
        )
      }
      disabled={subscribiliUser}
      options={CPT_CUSTOM_DISPLAY_LIST}
    />
  );

  const formatDiscountValueForPayload = (value) => {
    if (value === '' || value === null || value === undefined) return 0;

    const numericValue = Number(value);
    if (Number.isNaN(numericValue)) return 0;

    return Number.isInteger(numericValue) ? numericValue : String(value);
  };

  const getPayload = () => {
    let payload = {
      plan_package_id: planPackageId,
      categories: [],
      services: [],
    };

    // Always stamp `category_order` from the UI position at save time so the
    // backend stores a display order that matches what the user sees — even
    // when the backend originally returned no `category_order` values.
    const updatedCat = assignCategoryOrderByPosition(categoryList).map((category) => ({
      ...category,
      discount_type: 'discount_percent',
      discount_value: formatDiscountValueForPayload(category?.discount_value),
    }));

    const builtServices = buildServicesPayload(serviceList, customCodes);
    const payloadServices = builtServices.map((service) => ({
      ...service,
      discount_value: formatDiscountValueForPayload(service?.discount_value),
    }));

    if (noDiscount) {
      payload.discount_level = 'global';
      payload.global_discount = { discount_type: 'no_discount' };
    } else if (globalDiscount) {
      payload.discount_level = 'global';
      payload.global_discount = {
        discount_type: 'discount_percent',
        discount_value: formatDiscountValueForPayload(discount_value),
      };
      payload.categories = updatedCat;
      payload.services = payloadServices;
    } else if (categoryDiscount) {
      payload.discount_level = 'category';
      payload.categories = updatedCat;
      payload.services = payloadServices;
    }

    return payload;
  };

  const checkForErrors = () => {
    let hasErrors = false;

    if (!(viewOnly || formValid)) {
      dispatch({ type: HIGHLIGHT_ERRORS });

      hasErrors = true;
    }

    return hasErrors;
  };

  const handleSaveClick = async () => {
    const formHasErrors = checkForErrors();
    if (!planPackageId || formHasErrors) return;

    setIsLoading(true);

    if (lifeTimeValueChanged) {
      setExecuteHandleSubmit(true);
      return;
    }

    try {
      const payload = getPayload();

      const response = await USER_SERVICES.postPlanFeeSummary(
        planPackageId,
        level,
        levelId,
        payload,
      );

      if (response?.type === 'success') {
        dispatchGlobalNotification('success', 'Details updated successfully');
        fetchFeeSummary().then(() => { });
      } else {
        throw response;
      }
    } catch (err) {
      // Log error for debugging in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Category discounts save error:', err);
      }

      dispatchGlobalNotification(
        'error',
        err?.message || err?.error || 'Error updating the details',
      );
    }

    setIsLoading(false);
  };

  useEffect(() => {
    const hasInitData = Object.keys(initData).length > 0;

    const discountTypeChanged = hasChangedFromInitial(
      hasInitData,
      discount_level,
      initData?.discount_level || 'global',
    );
    const discountValueChanged = hasChangedFromInitial(
      hasInitData && globalDiscount,
      discount_value,
      initData?.discount_value,
    );
    const categoryValueChanged = hasChangedFromInitial(
      hasInitData,
      categoryList,
      initData?.categories || [],
    );

    let validServices = true;
    const customCodeChanged = hasChangedFromInitial(
      hasInitData,
      serviceList,
      initData?.services || [],
    );

    let validCategories = true;
    let validCustomCodes = true;

    const customCodesChanged = hasChangedFromInitial(
      hasInitData,
      customCodes,
      initData?.custom || [],
    );

    // Helper function to validate a single service
    const validateService = (service) => {
      return Object.keys(service).every(
        (sKey) =>
          sKey === 'retail_price' ||
          sKey === 'enableLifeTimeValue' ||
          sKey === 'discount_value' ||
          service[sKey],
      );
    };

    // Helper function to validate a single code
    const validateCode = (code) => {
      return Object.keys(code).every(
        (cKey) =>
          cKey === 'discount_value' ||
          cKey === 'discount_type' ||
          cKey === 'display_type' ||
          cKey === 'order' ||
          cKey === 'category_order' ||
          cKey === 'id' ||
          code[cKey],
      );
    };

    // Helper function to validate custom codes category
    const validateCustomCodesCategory = (category) => {
      const categoryValid = category.name && category.id;
      if (!categoryValid) return false;

      return category.codes?.every(validateCode) ?? true;
    };

    const validateCategory = (category) =>
      Boolean((category?.range_start ?? '').toString().trim()) &&
      Boolean((category?.range_end ?? '').toString().trim());

    if (customCodeChanged && serviceList.length) {
      validServices = serviceList.every(validateService);
    }

    // Always validate custom codes if they exist, regardless of whether they changed
    if (customCodes.length) {
      validCustomCodes = customCodes.every(validateCustomCodesCategory);
    }

    if (categoryList.length) {
      validCategories = categoryList.every(validateCategory);
    }

    const isGlobalDiscountValid =
      Boolean(discount_value) &&
      validServices &&
      validCustomCodes &&
      validCategories;
    const isCategoryDiscountValid = validCustomCodes && validServices && validCategories;
    // For no discount, we still need to validate custom codes if they exist
    const isNoDiscountValid = validCustomCodes && validServices;
    const validForm =
      (globalDiscount && isGlobalDiscountValid) ||
      (categoryDiscount && isCategoryDiscountValid) ||
      (noDiscount && isNoDiscountValid);

    updateCategoryValues({
      formUpdated:
        discountTypeChanged ||
        discountValueChanged ||
        categoryValueChanged ||
        customCodeChanged ||
        customCodesChanged,
      formValid: validForm,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initData, categoryList, serviceList, customCodes, discount_level, discount_value, categoryDiscount, globalDiscount]);


  useEffect(() => {
    setLifeTimeValueChanged(
      hasEnableLifeTimeValueChanged(initData?.services, serviceList),
    );
  }, [serviceList, initData?.services]);

  // Helper function to process services (filter out custom codes that have categories)
  const processServices = useCallback((allServices) => {
    const regularServices = allServices.filter((service) => !service.category);
    return regularServices.map((service) => ({
      ...service,
      enableLifeTimeValue: !exludedLifeTimeValueCodes?.includes(service?.code),
    }));
  }, [exludedLifeTimeValueCodes]);

  // Helper function to convert services to custom codes (handles template imports)
  const convertServicesToCustomCodes = useCallback((allServices) => {
    const customCodesFromServices = allServices.filter(
      (service) => service.category && String(service.category).trim() !== ''
    );

    if (customCodesFromServices.length === 0) return [];

    const customCodesMap = new Map();
    customCodesFromServices.forEach((service, index) => {
      const categoryKey = service.category;
      const category_order = service.category_order ?? service.order ?? 1;

      if (!customCodesMap.has(categoryKey)) {
        customCodesMap.set(categoryKey, {
          id: `cat_${toSlug(categoryKey)}_${index}`,
          name: service.category,
          category_order,
          codes: [],
        });
      }

      customCodesMap.get(categoryKey).codes.push({
        id: `code_${toSlug(service.code || '')}_${index}`,
        code: service.code || '',
        description: service.name || '',
        discount_type: service.discount_type || 'discount_percent',
        discount_value: service.discount_value ?? 0,
        display_type: service.display_type || 'fees_list',
        category_order,
      });
    });

    return Array.from(customCodesMap.values()).sort(
      (a, b) => (a.category_order ?? 1) - (b.category_order ?? 1)
    );
  }, []);


  // Helper function to process discount data
  const processDiscountData = useCallback((resp) => {
    const {
      discount_level: rawDiscountLevel,
      global_discount,
      categories: rawCategories = [],
    } = resp;

    const {
      discount_type: discountType,
      discount_value: globalDiscountValue = 0,
    } = global_discount || {};

    let discountLevel = rawDiscountLevel;
    let discountValue = globalDiscountValue;
    let categories = rawCategories;

    if (discountLevel === 'global' && discountType === 'no_discount') {
      discountLevel = 'no_discount';
      discountValue = 0;
    }

    if (discountLevel === 'global') {
      categories = rawCategories.map((category) => ({
        ...category,
        discount_value: globalDiscountValue,
      }));
    }

    // Order categories by `category_order` ascending, falling back to
    // `range_start` when there is a tie (e.g. backend sends category_order=1
    // for every row). This keeps the displayed order predictable.
    categories = sortCategoriesByOrder(categories);

    return { discountLevel, discountValue, categories };
  }, []);

  const resetDiscountValues = useCallback(() => {
    updateCategoryValues({
      initData: {},
      discount_level: 'global',
      discount_value: '',
      categories: [],
      services: [],
      custom: [],
    });
  }, [updateCategoryValues]);

  const fetchFeeSummary = useCallback(async () => {
    setCategoryLoading(true);

    try {
      if (!planPackageId || !level || !levelId) {
        resetDiscountValues();
        return;
      }

      const response = await USER_SERVICES.getPlanFeeSummary(planPackageId, level, levelId);

      if (response?.type === 'success' && response?.data) {
        const resp = response?.data?.rows?.[0] || {};
        let allServices = resp?.services || [];

        // Normalize: ensure every service with category has category_order (same for same category)
        allServices = normalizeCategoryOrderInServices(allServices);

        const services = processServices(allServices);
        const { discountLevel, discountValue, categories } = processDiscountData(resp);

        // Handle custom codes: merge from services (with category_order) and resp.custom so we never lose data
        let custom = resp?.custom || [];
        const customCodesFromServices = convertServicesToCustomCodes(allServices);
        const normalizedFromCustom = normalizeCategoryOrderInCustomCodes(custom);

        if (customCodesFromServices.length > 0 && normalizedFromCustom.length > 0) {
          const namesFromServices = new Set(customCodesFromServices.map((c) => c.name?.trim()).filter(Boolean));
          let nextOrder = Math.max(0, ...customCodesFromServices.map((c) => c.category_order ?? 0)) + 1;
          normalizedFromCustom.forEach((cat) => {
            const name = cat.name?.trim();
            if (name && !namesFromServices.has(name)) {
              namesFromServices.add(name);
              const catOrder = cat.category_order ?? nextOrder++;
              customCodesFromServices.push({
                ...cat,
                category_order: catOrder,
                codes: (cat.codes || []).map((code) => ({
                  ...code,
                  category_order: code.category_order ?? catOrder,
                })),
              });
            }
          });
          custom = customCodesFromServices.sort((a, b) => (a.category_order ?? 1) - (b.category_order ?? 1));
        } else if (customCodesFromServices.length > 0) {
          custom = customCodesFromServices;
        } else {
          custom = normalizedFromCustom;
        }

        const initData = {
          discount_level: discountLevel,
          discount_value: discountValue,
          categories,
          services,
          custom,
        };

        updateCategoryValues({ initData, ...initData });

        dispatch({
          type: 'PLANS_BY_EXCEL',
          payload: {
            isPlansByExcel: resp.summary_mode === 'excel_upload',
          },
        });
      } else {
        throw response;
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Fetch procedure category error:', err);
      }

      resetDiscountValues();
    } finally {
      setCategoryLoading(false);
    }
  }, [planPackageId, level, levelId, dispatch, processServices, processDiscountData, convertServicesToCustomCodes, resetDiscountValues]);

  useEffect(() => {
    if (levelId) {
      fetchFeeSummary();
    }
  }, [levelId, fetchFeeSummary]);

  useEffect(() => () => dispatch({ type: RESET_CATEGORY_DISCOUNTS }), [dispatch]);

  const errorForm = Boolean(highlightErrors && !formValid);
  const hasTableChanges = useMemo(
    () =>
      !_.isEqual(categoryList, initData?.categories || []) ||
      !_.isEqual(serviceList, initData?.services || []) ||
      !_.isEqual(customCodes, initData?.custom || []),
    [categoryList, initData, serviceList, customCodes]
  );

  const categoryData = useMemo(() =>
    categoryList.map((category) => ({
      ...category,
      code: formatDisplayRange(category?.range_start, category?.range_end),
      actions: category,
    })), [categoryList]
  );

  const flatCategoryData = useMemo(() =>
    categoryData.map((category, index) => ({
      ...category,
      orderNumber: `category-${category?.id || index}`,
      originalIndex: index,
    })), [categoryData]
  );
  const categoryRowGroups = useMemo(() =>
    flatCategoryData.map((category, index) => ({
      orderNumber: category.orderNumber,
      rows: [{ ...category, originalIndex: index }],
      startIndex: index,
      id: `category-row-${category.orderNumber}`,
      groupName: category?.name || '',
      height: 60,
    })), [flatCategoryData]
  );

  const servicesData = useMemo(() =>
    serviceList.map((service) => ({
      ...service,
      actions: service,
    })), [serviceList]
  );

  const tableData = useMemo(() => {
    switch (discount_level) {
      case 'category':
      case 'global':
        return [...categoryData, ...servicesData];
      default:
        return [];
    }
  }, [discount_level, categoryData, servicesData]);

  const xsmallCellProps = () => ({ className: classes.xsmallColumn });
  const smallCellProps = () => ({ className: classes.smallColumn });
  const mediumCellProps = () => ({ className: classes.mediumColumn });
  const largeCellProps = () => ({ className: classes.largeColumn });

  const renderCategoryRangeCell = useCallback((_value, tableMetaData) => {
    const { rowIndex } = tableMetaData;
    const category = categoryList[rowIndex] || {};
    const rangeStart = (category.range_start ?? '').toString();
    const rangeEnd = (category.range_end ?? '').toString();
    let rangeErrorMessage = '';
    if (errorForm) {
      const hasStart = Boolean((rangeStart ?? '').toString().trim());
      const hasEnd = Boolean((rangeEnd ?? '').toString().trim());
      if (!hasStart && !hasEnd) rangeErrorMessage = 'Start and end range required';
      else if (!hasStart) rangeErrorMessage = 'Start range required';
      else if (!hasEnd) rangeErrorMessage = 'End range required';
    }

    return (
      <RangeInputCell
        rangeStart={rangeStart}
        rangeEnd={rangeEnd}
        rowIndex={rowIndex}
        onChange={onCategoryFieldChange}
        classes={classes}
        disabled={subscribiliUser}
        error={Boolean(rangeErrorMessage)}
        errorMessage={rangeErrorMessage}
      />
    );
  }, [categoryList, errorForm, onCategoryFieldChange, classes, subscribiliUser]);

  const renderCategoryDescriptionCell = useCallback((value, tableMetaData) => {
    const { rowIndex } = tableMetaData;
    return (
      <TextFieldCellRenderer
        value={value}
        name="name"
        rowIndex={rowIndex}
        onChange={onCategoryFieldChange}
        classes={classes}
        inputProps={{ disabled: subscribiliUser }}
      />
    );
  }, [onCategoryFieldChange, classes, subscribiliUser]);

  const renderCategoryActionsCell = useCallback((_value, tableMetaData) => {
    const { rowIndex } = tableMetaData;
    return (
      <IconButton
        aria-label="delete-category-row"
        onClick={() => deleteCategory(rowIndex)}
        data-testid={`delete-category-row-button-${rowIndex}`}
        disabled={subscribiliUser}
      >
        <ClearIcon fontSize="inherit" />
      </IconButton>
    );
  }, [deleteCategory, subscribiliUser]);

  const categoryTableColumns = useMemo(() => [
    {
      name: '',
      label: '',
      options: {
        setCellHeaderProps: xsmallCellProps,
        sort: false,
        filter: false,
        customBodyRender: () => null,
      },
    },
    {
      name: 'list_no',
      label: 'List',
      options: {
        setCellHeaderProps: xsmallCellProps,
        customBodyRender: (_value, tableMetaData) => {
          const { rowIndex } = tableMetaData;
          return <RowNumberCellRenderer rowIndex={rowIndex} />;
        },
      },
    },
    {
      name: 'code',
      label: 'CPT Range',
      options: {
        setCellHeaderProps: largeCellProps,
        customBodyRender: renderCategoryRangeCell,
      },
    },
    {
      name: 'name',
      label: 'Description',
      options: {
        setCellHeaderProps: largeCellProps,
        customBodyRender: renderCategoryDescriptionCell,
      },
    },
    {
      name: 'discount_value',
      label: 'Discount Value',
      options: {
        setCellHeaderProps: smallCellProps,
        customBodyRender: (value, tableMetaData) => {
          const { rowIndex } = tableMetaData;
          const maxValue = 100;
          const disabled = globalDiscount || subscribiliUser;

          return (
            <TextFieldCellRenderer
              value={value}
              name="discount_value"
              rowIndex={rowIndex}
              onChange={onTextfieldChange}
              errorForm={errorForm}
              onWheel={(e) => e.target.blur()}
              placeholder={0}
              classes={classes}
              inputProps={{
                type: 'number',
                min: 0,
                step: 1,
                max: maxValue,
                disabled,
              }}
            />
          );
        },
      },
    },
    {
      name: 'discount_type',
      label: 'Discount Type',
      options: {
        setCellHeaderProps: smallCellProps,
        customBodyRender: (_value) => {
          return '%';
        },
      },
    },
    {
      name: 'actions',
      label: 'Actions',
      options: {
        setCellHeaderProps: smallCellProps,
        customBodyRender: renderCategoryActionsCell,
      },
    },
  ], [
    globalDiscount,
    subscribiliUser,
    categoryList,
    errorForm,
    classes,
    onCategoryFieldChange,
    onTextfieldChange,
    deleteCategory,
    renderCategoryRangeCell,
    renderCategoryDescriptionCell,
    renderCategoryActionsCell,
  ]);

  const excelCategoryTableColumns = useMemo(() => [
    {
      name: 'list_no',
      label: 'List',
      options: {
        setCellHeaderProps: xsmallCellProps,
        customBodyRender: (_value, tableMetaData) => {
          const { rowIndex } = tableMetaData;
          return <RowNumberCellRenderer rowIndex={rowIndex} />;
        },
      },
    },
    {
      name: 'code',
      label: 'CPT Code/Range',
      options: {
        setCellHeaderProps: smallCellProps,
      },
    },
    {
      name: 'name',
      label: 'Description',
      options: {
        setCellHeaderProps: mediumCellProps,
      },
    },
    {
      name: 'discount_value',
      label: 'Discount Percentage',
      options: {
        setCellHeaderProps: mediumCellProps,
        customBodyRender: (value) => value || '-',
      },
    },
    {
      name: 'discount_type',
      label: 'Discount Type',
      options: {
        setCellHeaderProps: smallCellProps,
        customBodyRender: () => '%',
      },
    },
  ], [classes]);

  // Services table columns with drag and drop support
  const serviceTableColumnsDnD = useMemo(() => [
    {
      name: '',
      label: '',
      options: {
        setCellHeaderProps: xsmallCellProps,
        sort: false,
        filter: false,
        customBodyRender: () => null, // Drag handle rendered by SortableRow
      },
    },
    {
      name: 'categoryOrder',
      label: 'List',
      options: {
        setCellHeaderProps: xsmallCellProps,
        customBodyRender: (value, tableMeta) => {
          const { rowIndex } = tableMeta;
          return (
            <CategoryOrderCellRenderer
              rowIndex={rowIndex}
              flatServicesData={flatServicesData}
              serviceGroups={serviceGroups}
            />
          );
        },
      },
    },
    {
      name: 'categoryName',
      label: 'Category *',
      options: {
        setCellHeaderProps: () => ({
          className: classes.largeColumn
        }),
        customBodyRender: (value, tableMeta) => {
          const { rowIndex } = tableMeta;
          return (
            <CategoryNameCellRenderer
              value={value}
              rowIndex={rowIndex}
              flatData={flatServicesData}
              fieldName="categoryName"
              onChange={onServiceFieldChange}
              errorForm={errorForm}
              classes={classes}
            />
          );
        },
      },
    },
    {
      name: 'code',
      label: 'CPT Code/Range',
      options: {
        setCellHeaderProps: mediumCellProps,
        customBodyRender: (value, tableMeta) => {
          const { rowIndex } = tableMeta;
          return (
            <TextFieldCellRenderer
              value={value}
              name="code"
              rowIndex={rowIndex}
              onChange={onServiceFieldChange}
              errorForm={errorForm}
              classes={classes}
              inputProps={{ disabled: subscribiliUser }}
            />
          );
        },
      },
    },
    {
      name: 'name',
      label: 'Description',
      options: {
        setCellHeaderProps: () => ({
          className: classes.largeColumn
        }),
        customBodyRender: (value, tableMeta) => {
          const { rowIndex } = tableMeta;
          return (
            <TextFieldCellRenderer
              value={value}
              name="name"
              rowIndex={rowIndex}
              onChange={onServiceFieldChange}
              errorForm={errorForm}
              variant="outlined"
              classes={classes}
            />
          );
        },
      },
    },
    {
      name: 'discount_value',
      label: 'Discount Value',
      options: {
        setCellHeaderProps: smallCellProps,
        customBodyRender: (value, tableMeta) => {
          const { rowIndex } = tableMeta;
          const maxValue =
            flatServicesData?.[rowIndex]?.discount_type === 'discount_percent' && 100;

          return (
            <TextFieldCellRenderer
              value={value}
              name="discount_value"
              rowIndex={rowIndex}
              onChange={onServiceFieldChange}
              errorForm={errorForm}
              onWheel={(e) => e.target.blur()}
              placeholder={0}
              classes={classes}
              inputProps={{
                type: 'number',
                min: 0,
                step: 1,
                max: maxValue,
                disabled: subscribiliUser,
              }}
            />
          );
        },
      },
    },
    {
      name: 'discount_type',
      label: 'Discount Type',
      options: {
        setCellHeaderProps: smallCellProps,
        customBodyRender: (value, tableMeta) => {
          const { rowIndex } = tableMeta;
          return (
            <ToggleCellRenderer
              rowIndex={rowIndex}
              isCustomCode={false}
              renderToggle={renderToggle}
            />
          );
        },
      },
    },
    {
      name: 'display_type',
      label: 'Display *',
      options: {
        setCellHeaderProps: smallCellProps,
        customBodyRender: (value, tableMeta) => {
          const { rowIndex } = tableMeta;
          return (
            <DisplaySelectCellRenderer
              value={value}
              name="display_type"
              rowIndex={rowIndex}
              renderSelect={renderServiceDisplaySelect}
            />
          );
        },
      },
    },
    {
      name: 'actions',
      label: 'Actions',
      options: {
        setCellHeaderProps: mediumCellProps,
        sort: false,
        filter: false,
        customBodyRender: (value, tableMeta) => {
          const { rowIndex } = tableMeta;
          return (
            <ActionsCellRenderer
              rowIndex={rowIndex}
              onDelete={deleteService}
              onAdd={addServiceRow}
              disabled={subscribiliUser}
            />
          );
        },
      },
    },
  ], [flatServicesData, serviceGroups, deleteService, addServiceRow, onServiceFieldChange, subscribiliUser, errorForm, classes]);

  // Custom codes table columns with drag and drop support
  const customCodesTableColumns = useMemo(() => [
    {
      name: '',
      label: '',
      options: {
        setCellHeaderProps: xsmallCellProps,
        sort: false,
        filter: false,
        customBodyRender: () => null,
      },
    },
    {
      name: 'categoryOrder',
      label: 'List',
      options: {
        setCellHeaderProps: xsmallCellProps,
        customBodyRender: (value, tableMeta) => {
          const { rowIndex } = tableMeta;
          return (
            <CustomCodeCategoryOrderCellRenderer
              rowIndex={rowIndex}
              flatCustomCodes={flatCustomCodes}
              categoryGroups={categoryGroups}
            />
          );
        },
      },
    },
    {
      name: 'categoryName',
      label: 'Category *',
      options: {
        setCellHeaderProps: () => ({
          className: classes.largeColumn,
        }),
        customBodyRender: (value, tableMeta) => {
          const { rowIndex } = tableMeta;
          return (
            <CategoryNameCellRenderer
              value={value}
              rowIndex={rowIndex}
              flatData={flatCustomCodes}
              fieldName="categoryName"
              onChange={onCustomCodeFieldChange}
              errorForm={errorForm}
              classes={classes}
            />
          );
        },
      },
    },
    {
      name: 'code',
      label: 'CPT Code',
      options: {
        setCellHeaderProps: largeCellProps,
        customBodyRender: (value, tableMeta) => {
          const { rowIndex } = tableMeta;
          return (
            <TextFieldCellRenderer
              value={value}
              name="code"
              rowIndex={rowIndex}
              onChange={onCustomCodeFieldChange}
              errorForm={errorForm}
              classes={classes}
              inputProps={{ disabled: subscribiliUser }}
            />
          );
        },
      },
    },
    {
      name: 'description',
      label: 'Description',
      options: {
        setCellHeaderProps: () => ({
          className: classes.largeColumn,
        }),
        customBodyRender: (value, tableMeta) => {
          const { rowIndex } = tableMeta;
          return (
            <TextFieldCellRenderer
              value={value}
              name="description"
              rowIndex={rowIndex}
              onChange={onCustomCodeFieldChange}
              errorForm={errorForm}
              variant="outlined"
              classes={classes}
              inputProps={{
                disabled: subscribiliUser,
              }}
            />
          );
        },
      },
    },
    {
      name: 'discount_value',
      label: 'Discount Value',
      options: {
        setCellHeaderProps: smallCellProps,
        customBodyRender: (value, tableMeta) => {
          const { rowIndex } = tableMeta;
          const maxValue =
            flatCustomCodes?.[rowIndex]?.discount_type === 'discount_percent' &&
            100;
          const disabled = subscribiliUser;

          return (
            <TextFieldCellRenderer
              value={value}
              name="discount_value"
              rowIndex={rowIndex}
              onChange={onCustomCodeFieldChange}
              errorForm={errorForm}
              onWheel={(e) => e.target.blur()}
              placeholder={0}
              classes={classes}
              inputProps={{
                type: 'number',
                min: 0,
                step: 1,
                max: maxValue,
                disabled,
              }}
            />
          );
        },
      },
    },
    {
      name: 'discount_type',
      label: 'Discount Type',
      options: {
        setCellHeaderProps: smallCellProps,
        customBodyRender: (_value, tableMeta) => {
          const { rowIndex } = tableMeta;
          return (
            <ToggleCellRenderer
              rowIndex={rowIndex}
              isCustomCode={true}
              renderToggle={renderToggle}
            />
          );
        },
      },
    },
    {
      name: 'display_type',
      label: 'Display *',
      options: {
        setCellHeaderProps: smallCellProps,
        customBodyRender: (value, tableMeta) => {
          const { rowIndex } = tableMeta;
          return (
            <DisplaySelectCellRenderer
              value={value}
              name="display_type"
              rowIndex={rowIndex}
              renderSelect={renderDisplaySelect}
            />
          );
        },
      },
    },
    {
      name: 'actions',
      label: 'Actions',
      options: {
        setCellHeaderProps: mediumCellProps,
        customBodyRender: (value, tableMeta) => {
          const { rowIndex } = tableMeta;
          return (
            <CustomCodeActionsCellRenderer
              rowIndex={rowIndex}
              onAdd={addCustomSubCode}
              onDelete={deleteCustomCode}
              disabled={subscribiliUser}
              showAdd={true}
            />
          );
        },
      },
    },
  ], [flatCustomCodes, categoryGroups, deleteCustomCode, subscribiliUser, errorForm, classes]);

  const addCategoryRow = () => {
    const clonedCategories = structuredClone(categoryList);
    const maxCategoryOrder = Math.max(
      0,
      ...clonedCategories.map((category) => category?.category_order || 0)
    );
    const nextCategoryOrder = maxCategoryOrder + 1;

    clonedCategories.push({
      code: '',
      name: '',
      range_start: '',
      range_end: '',
      discount_type: 'discount_percent',
      discount_value: globalDiscount ? Number(discount_value || 0) : 0,
      category_order: nextCategoryOrder,
      order: nextCategoryOrder,
    });

    updateCategoryValues({
      categories: clonedCategories,
      formUpdated: true,
    });
  };

  const importDefaultCategories = async () => {
    if (!level || !levelId) return;

    try {
      setIsLoading(true);
      const response = await USER_SERVICES.getPlanFeeSummary(
        '',
        level,
        levelId,
        { useDefault: true }
      );

      if (response?.type === 'success' && response?.data) {
        const resp = response?.data?.rows?.[0] || {};
        const importedCategories = Array.isArray(resp?.categories) ? resp.categories : [];

        updateCategoryValues({
          categories: importedCategories,
          formUpdated: true,
        });
        return;
      }

      throw response;
    } catch (err) {
      dispatchGlobalNotification(
        'error',
        err?.message || err?.error || 'Error importing default categories'
      );
    } finally {
      setIsLoading(false);
    }
  };

  const renderViewOnlyDiscounts = () => (
    <>
      {noDiscount && (
        <Typography className="mg_top_20">
          No discounts available for this plan
        </Typography>
      )}
    </>
  );

  const renderGlobalDiscountLabel = () => (
    <>
      <Grid item xs={12} className="d-flex align-center">
        <TextField
          required
          name="flat_discount_value"
          value={discount_value}
          onChange={(e) => onTextfieldChange(e)}
          variant="outlined"
          placeholder="0"
          error={highlightErrors && globalDiscount && !discount_value}
          className={classes.textFieldInput}
          inputProps={{
            type: 'number',
            min: 0,
            max: 100,
            step: 1,
            onWheel: (e) => e.target.blur(),
            'data-testid': 'category-input-discount-value-flat',
            className: classes.flatDiscountInput,
            disabled: categoryDiscount || subscribiliUser,
          }}
        />
        <Typography variant="h6" className={classes.percentLabel}>
          %
        </Typography>
        Flat Discount for all {showCategories ? 'categories' : 'procedures'}
      </Grid>

      {Boolean(highlightErrors && globalDiscount && !discount_value) && (
        <Grid item xs={12}>
          <FormHelperText error>
            Discount Value is required
          </FormHelperText>
        </Grid>
      )}
    </>
  );

  const renderDiscountRadioOptions = () => (
    <RadioGroup
      className={viewOnly ? 'pointer-events-none' : ''}
      aria-label="discount_types"
      name="Discount Types"
      value={discount_level}
      onChange={handleRadioChange}
    >
      <FormControlLabel
        value="no_discount"
        control={
          <Radio
            disabled={subscribiliUser}
            inputProps={{
              'data-testid': 'category-discount-radio-no-discount',
            }}
          />
        }
        label="No Discounts"
      />
      <FormControlLabel
        value="global"
        className={classes.flatChargeForm}
        control={
          <Radio
            disabled={subscribiliUser}
            inputProps={{
              'data-testid': 'category-discount-radio-flat-discount',
            }}
          />
        }
        label={renderGlobalDiscountLabel()}
      />
      {showCategories && (
        <FormControlLabel
          value="category"
          control={
            <Radio
              disabled={subscribiliUser}
              inputProps={{
                'data-testid': 'category-discount-radio-per-discount',
              }}
            />
          }
          label="Discounts by categories"
        />
      )}
    </RadioGroup>
  );

  const renderDiscountsType = () => {
    if (viewOnly) {
      return renderViewOnlyDiscounts();
    }

    return (
      <Grid container spacing={0} alignItems="flex-start">
        <Grid item className={classes.subtitle}>
          <Typography>Configure the plan discounts :</Typography>
        </Grid>
        <Grid item>
          {renderDiscountRadioOptions()}
        </Grid>
      </Grid>
    );
  };

  const renderSaveChangesButton = () => (
    <>
      {planPackageId && formUpdated && (
        <Grid item>
          {otherButtons}
          <Button
            onClick={handleSaveClick}
            color="secondary"
            variant="contained"
            size="large"
            data-testid={`category-discount-save-button`}
            disabled={isLoading || (!formValid && !hasTableChanges)}
          >
            {isLoading && (
              <CircularProgress size={25} className={'color_white'} />
            )}
            {!isLoading && 'Save Changes'}
          </Button>
        </Grid>
      )}
    </>
  );

  // Calculate closest pagination option for excel tables
  const getClosestPaginationOption = (rowCount) => {
    const paginationOptions = [10, 15, 100];
    const closest = paginationOptions.reduce(
      (prev, curr) => (Math.abs(curr - rowCount) < Math.abs(prev - rowCount) ? curr : prev),
      paginationOptions[0]
    );
    return Math.max(closest, rowCount); // Ensure we don't show less than total rows
  };

  const options = {
    filter: false,
    sort: false,
    selectableRows: 'none',
    responsive: 'standard',
    print: false,
    pagination: false,
    search: false,
    download: false,
    viewColumns: false,
    elevation: 0,
  };

  // Excel options with smart pagination
  const getExcelOptions = (dataLength) => ({
    ...options,
    pagination: true,
    rowsPerPage: getClosestPaginationOption(dataLength),
    rowsPerPageOptions: [10, 15, 100],
  });

  if (categoryLoading)
    return (
      <Grid container justifyContent="center" className="position-relative">
        <CircularProgress size={25} thickness={5} />
      </Grid>
    );

  return (
    <div
      className={clsx('widget-wrapper', isLoading && 'disable-form')}
      elevation={0}
    >
      <SectionHeader
        title="Procedure Discounts"
        titleVariant="h5"
        titleComponent="h5"
        titleJustify="flex-start"
        gutterBottom
        rightSection={
          viewOnly && (
            <Tooltip
              arrow
              title={
                <Typography variant="body1">
                  Discounts are driven by excel
                </Typography>
              }
            >
              <InfoIcon
                className={classes.infoIcon}
                fontSize="small"
                color="primary"
              />
            </Tooltip>
          )
        }
      />
      <Divider />

      <Grid
        container
        id="discount-info"
        spacing={1}
        className={classes.discountContainer}
        justifyContent="space-between"
      >
        <Grid item>{renderDiscountsType()}</Grid>

        <Grid item className="d-flex align-center">
          {renderSaveChangesButton()}
        </Grid>
      </Grid>

      {Boolean(!noDiscount) && (
        <Grid container spacing={2} id="category-discounts-table">
          <Grid item xs={12}>
            {showCategories && (
              <SectionHeader
                title="Category Data"
                titleVariant="h6"
                titleComponent="h6"
                titleJustify="space-between"
                className={classes.serviceDataHeader}
                rightSection={
                  !viewOnly && (
                    <ImportDropdownButton
                      label="Add Category"
                      options={['Restore to Default']}
                      onAdd={addCategoryRow}
                      onSelectOption={(option) => {
                        if (option === 'Restore to Default')
                          importDefaultCategories();
                      }}
                      editMode={false}
                    />
                  )
                }
              />
            )}
            {showCategories && (globalDiscount || categoryDiscount) && !viewOnly && (
              <DnDTable
                title=""
                flatData={flatCategoryData}
                tableColumns={categoryTableColumns}
                updateData={updateCategories}
                disabled={subscribiliUser}
                collapsedGroups={collapsedGroups}
                dataGroups={categoryRowGroups}
                onCollapseGroup={handleCollapseGroup}
                onExpandGroup={handleExpandGroup}
              />
            )}

            {/* Excel view for categories */}
            {showCategories && (globalDiscount || categoryDiscount) && viewOnly && (
              <MUIDataTable
                title=""
                data={categoryData}
                columns={excelCategoryTableColumns}
                options={getExcelOptions(categoryData.length)}
              />
            )}

            {!viewOnly && (
              <Grid item xs={12}>
                <SectionHeader
                  title="Custom Codes"
                  titleVariant="h6"
                  titleComponent="h6"
                  titleJustify="space-between"
                  className={classes.serviceDataHeader}
                  rightSection={
                    <div
                      style={{
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <ImportDropdownButton
                        label="Add Custom Code"
                        options={['Import from File']}
                        onAdd={plan_id ? addEditCustomCode : addCustomCode}
                        onSelectOption={(option) => {
                          if (option === 'Import from File')
                            fileInputRef.current?.click();
                        }}
                        editMode={false}
                      />
                      <input
                        type="file"
                        ref={fileInputRef}
                        accept={ALLOWED_IMPORT_MIME_TYPES.join(',')}
                        style={{ display: 'none' }}
                        onChange={handleImportFile}
                      />
                    </div>
                  }
                />
                <Paper
                  className={classes.customCodesDownloadWrapper}
                  elevation={0}
                >
                  <Typography component="span" className="mr-10">
                    Need Help?{' '}
                    <Typography
                      data-testid="download-custom-codes-template-link"
                      onClick={handleCustomCodesTemplateDownload}
                      color="secondary"
                      component="span"
                      style={{ cursor: 'pointer' }}
                    >
                      Download our file template
                    </Typography>{' '}
                    to make sure your file is formatted correctly.
                  </Typography>
                  {templateDownloading && (
                    <Typography
                      component="span"
                      style={{ display: 'inline-flex' }}
                    >
                      <CircularProgress
                        size={16}
                        className="theme_color_orange"
                      />
                    </Typography>
                  )}
                </Paper>
                <Grid
                  container
                  justifyContent="space-between"
                  style={{ paddingTop: 16, paddingBottom: 16 }}
                >
                  <Grid item xs={6} className={classes.customCodesGuidelineText}>
                    <Typography align="left">
                      Supported format: {CUSTOM_CODES_FILE_TYPES.join(', ')}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} className={classes.customCodesGuidelineText}>
                    <Typography align="right">
                      Maximum Size: {MAX_FILE_SIZE_MB}MB
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            )}

            {/* Services table with drag and drop - when no custom codes exist */}
            {servicesData && servicesData.length > 0 && !viewOnly && (
              <DnDTable
                flatData={flatServicesData}
                tableColumns={serviceTableColumnsDnD}
                updateData={updateServices}
                disabled={subscribiliUser}
                collapsedGroups={collapsedGroups}
                dataGroups={serviceGroups}
                onCollapseGroup={handleCollapseGroup}
                onExpandGroup={handleExpandGroup}
              />
            )}

            {/* Custom codes table with drag and drop - show if custom codes exist */}
            {customCodes && customCodes.length > 0 && !viewOnly && (
              <DnDTable
                title=""
                flatData={flatCustomCodes}
                tableColumns={customCodesTableColumns}
                updateData={updateCustomCodes}
                disabled={subscribiliUser}
                collapsedGroups={collapsedGroups}
                dataGroups={categoryGroups}
                onCollapseGroup={handleCollapseGroup}
                onExpandGroup={handleExpandGroup}
              />
            )}

            {/* Excel view for services */}
            {servicesData && servicesData.length > 0 && !(customCodes && customCodes.length > 0) && viewOnly && (
              <MUIDataTable
                data={servicesData}
                columns={serviceTableColumnsDnD.filter(col =>
                  !['actions', ''].includes(col.name) // Remove drag handle and actions columns for view-only
                )}
                options={getExcelOptions(servicesData.length)}
              />
            )}

            {/* Excel view for custom codes */}
            {customCodes && customCodes.length > 0 && viewOnly && (
              <MUIDataTable
                title="Custom Codes"
                data={flatCustomCodes}
                columns={customCodesTableColumns.filter(col =>
                  !['actions', ''].includes(col.name) // Remove drag handle and actions columns for view-only
                )}
                options={getExcelOptions(flatCustomCodes.length)}
              />
            )}

            {/* Show empty state when no data exists */}
            {!viewOnly && (!servicesData || servicesData.length === 0) && (!customCodes || customCodes.length === 0) && (
              <MUIDataTable
                title=""
                data={[]}
                columns={customCodesTableColumns}
                options={options}
              />
            )}


          </Grid>

          <Grid item xs={12}>
            <Grid
              container
              spacing={0}
              alignItems="center"
              justifyContent={'flex-end'}
            >
              {renderSaveChangesButton()}
              <Grid item>
                {errorForm && (
                  <Typography className={classes.errorText} color="error">
                    * Please fill the required fields
                  </Typography>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </div>
  );
};

CategoryDiscounts.propTypes = {
  level: PropTypes.string,
  levelId: PropTypes.number,
  planPackageId: PropTypes.number,
  otherButtons: PropTypes.string,
  viewOnly: PropTypes.bool,
  setExecuteHandleSubmit: PropTypes.func,
  showCategoryDiscounts: PropTypes.bool,
};

export default CategoryDiscounts;